import { useMemo } from 'react';
import {
  FINGER_COLORS,
  getKeyboardLayout,
  isCharUnlocked,
} from '../data/keyboardLayouts';
import type { KeyDef, KeyboardLayoutId } from '../types';
import { formatKeyLabel } from '../lib/typingProfile';

interface SelectableKeyboardProps {
  layoutId: KeyboardLayoutId;
  unlockedKeys: ReadonlyArray<string>;
  selectedKeys: ReadonlyArray<string>;
  maxSelected: number;
  onToggleKey: (key: string) => void;
}

export default function SelectableKeyboard({
  layoutId,
  unlockedKeys,
  selectedKeys,
  maxSelected,
  onToggleKey,
}: SelectableKeyboardProps) {
  const layout = useMemo(() => getKeyboardLayout(layoutId), [layoutId]);
  const selected = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const atMax = selectedKeys.length >= maxSelected;

  return (
    <div className="w-full">
      <div className="mx-auto flex max-w-4xl flex-col gap-1 surface p-3">
        {layout.map((row, ri) => (
          <div key={ri} className="flex gap-1">
            {row.map((k, ki) => (
              <SelectableKeyCap
                key={ki}
                keyDef={k}
                unlockedKeys={unlockedKeys}
                maxSelected={maxSelected}
                selected={!k.decorative && selected.has(practiceKeyForDef(k))}
                disabled={
                  !k.decorative &&
                  atMax &&
                  !selected.has(practiceKeyForDef(k))
                }
                onToggle={onToggleKey}
              />
            ))}
          </div>
        ))}
      </div>
      {selectedKeys.length > 0 && (
        <p className="mt-3 text-center text-sm text-muted">
          Valgt:{' '}
          {selectedKeys.map((k) => formatKeyLabel(k)).join(' · ')}
        </p>
      )}
    </div>
  );
}

function practiceKeyForDef(k: KeyDef): string {
  if (k.decorative) return '';
  return k.char;
}

interface SelectableKeyCapProps {
  keyDef: KeyDef;
  unlockedKeys: ReadonlyArray<string>;
  maxSelected: number;
  selected: boolean;
  disabled: boolean;
  onToggle: (key: string) => void;
}

function SelectableKeyCap({
  keyDef,
  unlockedKeys,
  maxSelected,
  selected,
  disabled,
  onToggle,
}: SelectableKeyCapProps) {
  const width = keyDef.width ?? 1;

  if (keyDef.decorative) {
    return (
      <div
        className="kc-base kc-decorative"
        style={{ flex: `${width} 1 0`, minWidth: 0 }}
        aria-hidden
      />
    );
  }

  const practiceKey = keyDef.char;
  const unlocked =
    isCharUnlocked(keyDef.char, unlockedKeys) ||
    (keyDef.shift ? isCharUnlocked(keyDef.shift, unlockedKeys) : false);

  const label =
    keyDef.label !== undefined
      ? keyDef.label
      : keyDef.char === ' '
        ? ''
        : keyDef.char;

  let stateClass = 'kc-locked opacity-40';
  if (unlocked) {
    stateClass = selected
      ? 'kc-expected-strong ring-2 ring-indigo-500 ring-offset-1 ring-offset-white dark:ring-offset-zinc-900'
      : 'kc-unlocked cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-950/40';
  }

  return (
    <button
      type="button"
      disabled={!unlocked || disabled}
      onClick={() => onToggle(practiceKey)}
      className={`kc-base ${stateClass} transition`}
      style={{ flex: `${width} 1 0`, minWidth: 0 }}
      aria-pressed={selected}
      aria-label={formatKeyLabel(practiceKey)}
      title={
        !unlocked
          ? 'Ikke låst op endnu'
          : disabled
            ? `Højst ${maxSelected} taster`
            : selected
              ? 'Fjern valg'
              : 'Vælg tast'
      }
    >
      <span className="truncate px-1">
        {keyDef.char === ' ' ? '' : (label || '').toUpperCase()}
      </span>
      {selected && (
        <span
          className={`absolute -bottom-1 h-1.5 w-1.5 rounded-full ${FINGER_COLORS[keyDef.finger]}`}
        />
      )}
    </button>
  );
}
